import { ReactElement } from "react";
import { Box, ButtonBase, SxProps, Theme } from "@mui/material";
import { SystemStyleObject } from "@mui/system";
import CheckRoundedIcon from "@mui/icons-material/CheckRounded";
import LockRoundedIcon from "@mui/icons-material/LockRounded";
import { CORRECT, ON_CORRECT, PulseDot } from "./thegamecommon";

const pillSx: SystemStyleObject<Theme> = {
  height: 44,
  width: "100%",
  minWidth: 0,
  padding: "0 8px",
  gap: "6px",
  borderRadius: "var(--sign-border-radius)",
  fontFamily: "var(--font-body)",
  fontSize: 16,
  fontWeight: 500,
  lineHeight: "18px",
  textAlign: "center",
  background: "var(--thegame-pill-background, var(--sign-background-not-selected))",
  color: "var(--sign-color)",
  transition: "background .12s, color .12s",
  "&.Mui-disabled": {
    opacity: 0.6,
  },
};

const interactiveSx: SystemStyleObject<Theme> = {
  "@media (hover: hover)": {
    "&:hover": {
      background: "var(--thegame-pill-hover-background, var(--sign-hover-background))",
      color: "var(--thegame-pill-hover-color, var(--sign-color-selected))",
    },
  },
};

const mineSx: SystemStyleObject<Theme> = {
  background: "var(--sign-background)",
  color: "var(--sign-color-selected)",
};

const leadSx: SystemStyleObject<Theme> = {
  boxShadow: `0 0 0 2px var(--background_4), 0 0 0 4px ${CORRECT}`,
};

const lockedSx: SystemStyleObject<Theme> = {
  background: CORRECT,
  color: ON_CORRECT,
};

const outSx: SystemStyleObject<Theme> = {
  opacity: 0.45,
};

const shareLayoutSx: SystemStyleObject<Theme> = {
  position: "relative",
  overflow: "hidden",
  justifyContent: "space-between",
  padding: "0 10px",
  fontSize: 15,
};

interface TheGamePillProps {
  label: string;
  mine: boolean;
  // Correct right now, not yet decided.
  lead?: boolean;
  // Decided and this is the result.
  locked?: boolean;
  // Decided and this is not the result.
  out?: boolean;
  // Share of all rows (0..1) on this selection; renders the spectator bar.
  share?: number;
  disabled?: boolean;
  onClick?: () => void;
}

export default function TheGamePill({ label, mine, lead, locked, out, share, disabled, onClick }: TheGamePillProps): ReactElement {
  const readOnly = !onClick;
  const hasShare = share !== undefined;
  const sx: SxProps<Theme> = [
    pillSx,
    readOnly ? { cursor: "default" } : interactiveSx,
    hasShare ? shareLayoutSx : {},
    mine ? mineSx : {},
    lead ? leadSx : {},
    lead && hasShare && !mine ? { color: CORRECT } : {},
    locked ? lockedSx : {},
    out ? outSx : {},
  ];

  const content = (
    <>
      {locked && (mine ? <CheckRoundedIcon sx={{ fontSize: 16 }} /> : <LockRoundedIcon sx={{ fontSize: 14 }} />)}
      <Box component="span" sx={out ? { textDecoration: "line-through" } : undefined}>{label}</Box>
      {lead && <PulseDot color={CORRECT} />}
    </>
  );

  return (
    <ButtonBase
      type="button"
      aria-pressed={mine}
      disabled={disabled}
      disableRipple={readOnly}
      tabIndex={readOnly ? -1 : undefined}
      onClick={onClick}
      sx={sx}
    >
      {hasShare ? (
        <>
          <Box
            component="span"
            sx={{
              position: "absolute",
              left: 0,
              top: 0,
              bottom: 0,
              width: `${Math.round(share * 100)}%`,
              background: locked ? "rgba(255, 255, 255, .18)" : "currentColor",
              opacity: locked ? 1 : 0.14,
            }}
          />
          <Box component="span" sx={{ position: "relative", display: "flex", alignItems: "center", gap: "6px", minWidth: 0 }}>
            {content}
          </Box>
          <Box component="span" sx={{ position: "relative", fontSize: 13, fontWeight: 600, opacity: 0.85 }}>
            {Math.round(share * 100)}%
          </Box>
        </>
      ) : (
        content
      )}
    </ButtonBase>
  );
}
